import { supabase } from './supabase';

/**
 * Campaign Service - handles all campaign-related database operations
 */
class CampaignService {
  /**
   * Check current authentication status
   * @returns {Promise<Object>} Auth status info
   */
  async checkAuth() {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      console.log('Current auth user:', user ? user.id : 'Not authenticated');
      return { user, error };
    } catch (error) {
      console.error('Error checking auth:', error);
      return { user: null, error };
    }
  }
  /**
   * Get campaign for current organization
   * @param {string} organizationId - Organization ID
   * @returns {Promise<Object|null>} Campaign data or null if not found
   */
  async getCampaign(organizationId) {
    try {
      console.log('Fetching campaign for organization:', organizationId);
      
      // Check authentication status
      await this.checkAuth();
      
      const { data, error } = await supabase
        .from('campaigns')
        .select('*')
        .eq('organization_id', organizationId)
        .single();

      if (error && error.code !== 'PGRST116') {
        // PGRST116 is "not found" error, which is expected for new organizations
        console.error('Supabase error fetching campaign:', error);
        console.error('Error details:', { code: error.code, message: error.message, details: error.details });
        throw error;
      }

      console.log('Campaign data retrieved:', data ? 'Found' : 'Not found');
      
      // Transform questions data to ensure proper frontend structure
      if (data && data.questions) {
        data.questions = this.normalizeQuestions(data.questions);
      }
      
      // Ensure platform_urls exists with default structure
      if (data && !data.platform_urls) {
        data.platform_urls = {
          google: '',
          yelp: '',
          facebook: ''
        };
      }
      
      return data;
    } catch (error) {
      console.error('Error fetching campaign:', error);
      throw error;
    }
  }

  /**
   * Normalize questions data to ensure proper structure with enabled field
   * @param {Array} questions - Questions array from database
   * @returns {Array} Normalized questions with enabled field
   */
  normalizeQuestions(questions) {
    const defaultQuestions = this.getDefaultCampaign().questions;
    
    console.log('normalizeQuestions - Input questions:', questions);
    console.log('normalizeQuestions - Default questions:', defaultQuestions);
    
    // If questions don't have 'enabled' field, we need to reconstruct
    const hasEnabledField = questions.length > 0 && questions[0].hasOwnProperty('enabled');
    
    console.log('normalizeQuestions - Has enabled field:', hasEnabledField);
    
    if (hasEnabledField) {
      // Questions already have proper structure
      console.log('normalizeQuestions - Using existing structure');
      return questions;
    }
    
    // Questions are missing 'enabled' field, reconstruct from defaults
    console.log('normalizeQuestions - Reconstructing from defaults...');
    
    const normalizedQuestions = defaultQuestions.map(defaultQ => {
      // Find matching question in saved data
      const savedQ = questions.find(q => q.label === defaultQ.label);
      
      const normalized = {
        label: defaultQ.label,
        enabled: savedQ ? true : false, // If it was saved, it was enabled
        required: savedQ?.required || defaultQ.required
      };
      
      console.log(`normalizeQuestions - ${defaultQ.label}: enabled=${normalized.enabled}, found in saved=${!!savedQ}`);
      
      return normalized;
    });
    
    console.log('normalizeQuestions - Final normalized questions:', normalizedQuestions);
    return normalizedQuestions;
  }

  /**
   * Create a new campaign for organization
   * @param {string} organizationId - Organization ID
   * @param {Object} campaignData - Campaign configuration
   * @returns {Promise<Object>} Created campaign
   */
  async createCampaign(organizationId, campaignData) {
    try {
      console.log('Creating new campaign for org:', organizationId);
      
      await this.checkAuth();
      
      const { data, error } = await supabase
        .from('campaigns')
        .insert({
          organization_id: organizationId,
          ...campaignData
        })
        .select()
        .single();

      if (error) {
        console.error('Supabase error creating campaign:', error);
        console.error('Error details:', { code: error.code, message: error.message, details: error.details });
        throw error;
      }
      
      console.log('Campaign created successfully:', data?.id);
      return data;
    } catch (error) {
      console.error('Error creating campaign:', error);
      throw error;
    }
  }

  /**
   * Update existing campaign
   * @param {string} organizationId - Organization ID
   * @param {Object} updates - Campaign updates
   * @returns {Promise<Object>} Updated campaign
   */
  async updateCampaign(organizationId, updates) {
    try {
      console.log('Updating campaign for org:', organizationId);
      console.log('Updates to apply:', updates);
      
      await this.checkAuth();
      
      const { data, error } = await supabase
        .from('campaigns')
        .update(updates)
        .eq('organization_id', organizationId)
        .select()
        .single();

      if (error) {
        console.error('Supabase error updating campaign:', error);
        console.error('Error details:', { code: error.code, message: error.message, details: error.details });
        throw error;
      }
      
      console.log('Campaign updated successfully:', data?.id);
      return data;
    } catch (error) {
      console.error('Error updating campaign:', error);
      throw error;
    }
  }

  /**
   * Upsert campaign (create or update)
   * @param {string} organizationId - Organization ID
   * @param {Object} campaignData - Campaign configuration
   * @returns {Promise<Object>} Campaign data
   */
  async upsertCampaign(organizationId, campaignData) {
    try {
      // Try to get existing campaign first
      const existing = await this.getCampaign(organizationId);
      
      if (existing) {
        // Update existing campaign
        return await this.updateCampaign(organizationId, campaignData);
      } else {
        // Create new campaign
        return await this.createCampaign(organizationId, campaignData);
      }
    } catch (error) {
      console.error('Error upserting campaign:', error);
      throw error;
    }
  }

  /**
   * Update specific step of campaign
   * @param {string} organizationId - Organization ID
   * @param {number} step - Step number (1, 2, or 3)
   * @param {Object} stepData - Step-specific data
   * @returns {Promise<Object>} Updated campaign
   */
  async updateCampaignStep(organizationId, step, stepData) {
    try {
      console.log(`Updating campaign step ${step} for org:`, organizationId);
      console.log('Step data:', stepData);

      // First, check if campaign exists
      let campaign = await this.getCampaign(organizationId);
      
      if (!campaign) {
        console.log('No existing campaign found, creating new one...');
        // Create default campaign first
        const defaultCampaign = this.getDefaultCampaign();
        campaign = await this.createCampaign(organizationId, defaultCampaign);
        console.log('Created new campaign:', campaign.id);
      }

      // Now update the specific step data
      let updates = {};

      switch (step) {
        case 1:
          // Step 1: Review Questions & Rating Logic
          if (stepData.questions) {
            console.log('Step 1 - Raw questions received:', stepData.questions);
            console.log('Step 1 - Questions structure check:', stepData.questions.map(q => ({
              label: q.label,
              enabled: q.enabled,
              required: q.required
            })));
            
            // Filter to only save enabled questions for database efficiency
            const enabledQuestions = stepData.questions
              .filter(q => q.enabled)
              .map(q => ({
                label: q.label,
                required: q.required
              }));
            
            console.log('Step 1 - Filtered enabled questions:', enabledQuestions);
            console.log('Step 1 - Number of enabled questions:', enabledQuestions.length);
            
            updates.questions = enabledQuestions;
          }
          if (stepData.positive_threshold) updates.positive_threshold = stepData.positive_threshold;
          break;

        case 2:
          // Step 2: Email Content
          if (stepData.email_title) updates.email_title = stepData.email_title;
          if (stepData.email_subject) updates.email_subject = stepData.email_subject;
          if (stepData.email_body_template) updates.email_body_template = stepData.email_body_template;
          if (stepData.cta_button_text) updates.cta_button_text = stepData.cta_button_text;
          break;

        case 3:
          // Step 3: Thank You Pages & Platform Selection
          if (stepData.thank_you_pages) updates.thank_you_pages = stepData.thank_you_pages;
          if (stepData.selected_review_platform) updates.selected_review_platform = stepData.selected_review_platform;
          if (stepData.platform_urls) updates.platform_urls = stepData.platform_urls;
          
          // Migration: handle legacy review_platform_url
          if (stepData.review_platform_url !== undefined) {
            console.log('Migrating legacy review_platform_url to platform_urls structure');
            const platform = stepData.selected_review_platform || campaign.selected_review_platform || 'google';
            updates.platform_urls = {
              ...campaign.platform_urls,
              [platform]: stepData.review_platform_url
            };
            // Keep the old field for backward compatibility during transition
            updates.review_platform_url = stepData.review_platform_url;
          }
          break;

        default:
          throw new Error(`Invalid step number: ${step}`);
      }

      console.log('Applying updates:', updates);
      return await this.updateCampaign(organizationId, updates);
    } catch (error) {
      console.error(`Error updating campaign step ${step}:`, error);
      throw error;
    }
  }

  /**
   * Delete campaign for organization
   * @param {string} organizationId - Organization ID
   * @returns {Promise<boolean>} Success status
   */
  async deleteCampaign(organizationId) {
    try {
      const { error } = await supabase
        .from('campaigns')
        .delete()
        .eq('organization_id', organizationId);

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error deleting campaign:', error);
      throw error;
    }
  }

  /**
   * Get default campaign configuration
   * @returns {Object} Default campaign data
   */
  getDefaultCampaign() {
    return {
      questions: [
        { label: "Overall Experience", enabled: true, required: true },
        { label: "Quality of Service", enabled: true, required: false },
        { label: "Professionalism & Courtesy", enabled: false, required: false },
        { label: "Value for Money", enabled: false, required: false },
        { label: "Cleanliness & Ambiance", enabled: false, required: false },
        { label: "Punctuality", enabled: false, required: false }
      ],
      positive_threshold: 4,
      email_title: 'How was our service?',
      email_subject: 'How was your experience with {{business.name}}?',
      email_body_template: `Hi {{contact.name}},

We hope you enjoyed your recent experience with {{business.name}}. 

Your feedback helps us continue to improve our services and provide the best possible experience for all our customers.

Could you take a moment to share your thoughts?`,
      cta_button_text: 'Leave us a review',
      thank_you_pages: {
        low: {
          headline: "We're sorry you had a poor experience",
          message: "Your feedback is important to us. Please let us know what went wrong so we can make it right.",
          buttonText: "Contact Us"
        },
        neutral: {
          headline: "Thank you for your feedback!",
          message: "We appreciate you taking the time to share your experience with us. Your feedback helps us continue to improve our services."
        },
        high: {
          headline: "Help others by sharing your experience!",
          message: "We've generated a review draft based on your feedback. Feel free to copy and customize it, then share it on your preferred platform."
        }
      },
      selected_review_platform: 'google',
      review_platform_url: '',
      platform_urls: {
        google: '',
        yelp: '',
        facebook: ''
      },
      ai_review_enabled: true
    };
  }
}

// Export singleton instance
const campaignService = new CampaignService();
export default campaignService; 